import React from "react";
import { formatAccountName } from "../../utils/formatters";

const HouseholdSummary = ({ householdData }) => {
  const members = householdData?.household_members || householdData?.members || [];
  const accounts = householdData?.accounts || [];
  const bankAccounts = householdData?.bank_accounts || [];

  const getMemberDisplayName = (member) => {
    let pi = member.personal_info || {};
    if (typeof pi === "string") {
      try { pi = JSON.parse(pi); } catch { pi = {}; }
    }
    const first = pi.first_name || "";
    const last = pi.last_name || "";
    const name = `${first} ${last}`.trim() || "Unknown";
    return pi.is_deceased ? `${name} (Deceased)` : name;
  };

  const getBankAccountDisplayName = (ba) => {
    const name = ba.bank_name || "Bank";
    const num = ba.account_number || "";
    return num ? `${name} — ${num}` : name;
  };

  // Sort members: primary first
  const sortedMembers = [...members].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return 0;
  });

  const columns = [
    {
      label: "Members",
      count: members.length,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      items: sortedMembers.map((m) => ({
        name: getMemberDisplayName(m),
        tag: m.is_primary ? "Primary" : null,
      })),
    },
    {
      label: "Accounts",
      count: accounts.length,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      items: accounts.map((a, i) => ({
        name: formatAccountName(a, i + 1, members),
        tag: null,
      })),
    },
    {
      label: "Bank Accounts",
      count: bankAccounts.length,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      items: bankAccounts.map((ba) => ({
        name: getBankAccountDisplayName(ba),
        tag: null,
      })),
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-[1.0625rem] font-bold text-gray-900">Household Summary</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
        {columns.map((col) => (
          <div key={col.label} className="px-5 py-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600">
                {col.icon}
              </div>
              <span className="text-sm font-semibold text-gray-700">{col.label}</span>
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                {col.count}
              </span>
            </div>
            <div className="border-t border-gray-100 mt-1" />
            {col.items.length > 0 ? (
              <div className="flex flex-col divide-y divide-gray-100">
                {col.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 py-2">
                    <span className="text-sm text-gray-600">{item.name}</span>
                    {item.tag && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-50 text-emerald-600 border border-emerald-100 flex-shrink-0">
                        {item.tag}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic mt-2">None</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HouseholdSummary;
